import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Grid,
  Tab,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useNavigate } from "react-router-dom";
import { GET_EMPLOYEE_TRAINING_MODULES } from "../../../apis/training-apis/trainingApis";
import { useQuery } from "@apollo/client";
import { GET_MASTER_LOOKUP_BY_TYPE } from "../../../apis/common-apis/commonApis";
import { MasterLookupItemsType } from "../../../utils/enums";
import {
  TabNames,
  TabValues,
  TrainingSectionAttemptStatusKey,
} from "../utils/enums";
import TrainingCard from "../TrainingComponents/TrainingCard";
import { useTheme } from "@mui/material/styles";
import InformationStepsCard from "../../../components/InformationStepsCard";

const TrainingEmployeeHome = () => {
  const [tabName, setTabName] = useState(TabNames.PENDING_TRAININGS);
  const [tab, setTab] = useState(TabValues.PENDING);
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: masterLookup, loading: masterLookupLoading } = useQuery(
    GET_MASTER_LOOKUP_BY_TYPE,
    {
      variables: {
        type: MasterLookupItemsType.TRAINING_SECTION_ATTEMPT_STATUS,
      },
    }
  );

  const {
    data: trainingModulesData,
    loading: isTrainingModulesDataLoading,
    refetch: refetchTrainingModules,
  } = useQuery(GET_EMPLOYEE_TRAINING_MODULES, {
    variables: { page: 1, limit: 1000 },
  });

  // Refetch training modules when component mounts
  useEffect(() => {
    refetchTrainingModules();
  }, [refetchTrainingModules]);

  if (isTrainingModulesDataLoading || masterLookupLoading) {
    return (
      <Container
        component="main"
        maxWidth="xs"
        sx={{ textAlign: "center", mt: 8 }}
      >
        <CircularProgress />
      </Container>
    );
  }

  const trainingModules =
    trainingModulesData?.getEmployeeTrainingModules?.data ?? [];
  const masterLookupData = masterLookup?.getMasterLookupByType?.data ?? [];

  const TrainingSectionAttemptStatusMap = Object.fromEntries(
    masterLookupData?.map((item) => [item?.key, item?.title]) ?? []
  );

  const pendingTrainings =
    trainingModules?.filter(
      (module) =>
        module?.completion_status_key ===
        TrainingSectionAttemptStatusKey.NOT_STARTED ||
        module?.completion_status_key ===
        TrainingSectionAttemptStatusKey.IN_PROGRESS
    ) ?? [];

  const completedTrainings =
    trainingModules?.filter(
      (module) =>
        module?.completion_status_key ===
        TrainingSectionAttemptStatusKey.COMPLETED
    ) ?? [];

  const tabNames = {
    [TabValues.PENDING]: TabNames.PENDING_TRAININGS,
    [TabValues.COMPLETED]: TabNames.COMPLETED_TRAININGS,
  };

  const handleChange = (event, newTab) => {
    setTab(newTab);
    setTabName(tabNames[newTab]);
  };

  const handleOnClick = (training) => {
    const formattedTitle = training?.title?.replace(/\s+/g, "-");
    navigate(`/training/${formattedTitle}?id=${training?.id}`);
  };

  return (
    <>
      <Box sx={{ paddingTop: "20px" }}>
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <InformationStepsCard
            heading="**What should I do on this page?**"
            subtitle="This page helps you complete the security, privacy, and compliance trainings assigned to you."
            steps={[
              "Review the trainings assigned to you under the **Pending trainings** section",
              "Open each training module to go through the material carefully and complete the relevant steps.",
              "Once you finish a training, the training will be marked as completed and visible in the **Completed trainings** section.",
              "All assigned trainings must be completed within 30 days of joining the organization or once every year as part of the annual compliance cycle.",
              "If you need help accessing or understanding any training, contact your manager or the compliance team.",
            ]}
            footerText="💡   Completing your trainings on time helps build a strong culture of security and ensures the organization stays compliant with regulatory requirements."
            collapsible={true}
            defaultExpanded={false}
          />
        </Box>
        <Box>
          <TabContext value={tab}>
            <Box
              sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}
            >
              <Typography
                variant="h4"
                fontWeight="600"
                color={theme.customColors.text.primary}
                sx={{ mb: 0.5 }}
              >
                {tabName}
              </Typography>
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <TabList onChange={handleChange} aria-label="Training Courses">
                <Tab
                  label={`Pending Trainings (${pendingTrainings?.length ?? 0})`}
                  value={TabValues.PENDING}
                />
                <Tab
                  label={`Completed Trainings (${completedTrainings?.length ?? 0
                    })`}
                  value={TabValues.COMPLETED}
                />
              </TabList>
            </Box>
            <TabPanel value="1" sx={{ p: 0 }}>
              {(pendingTrainings?.length ?? 0) > 0 ? (
                <>
                  <Typography
                    variant="body1"
                    sx={{ color: "text.secondary", marginBottom: "20px" }}
                  >
                    Please complete these trainings before the due date.
                  </Typography>
                  <Grid container spacing={2}>
                    {pendingTrainings?.map((training, index) => (
                      <Grid item xs={12} key={training?.id ?? index}>
                        <TrainingCard
                          training={training}
                          isAdmin={false}
                          onClick={handleOnClick}
                          TrainingSectionAttemptStatusMap={
                            TrainingSectionAttemptStatusMap
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              ) : (
                <Box
                  sx={{
                    height: "300px", // Or match your card/grid height
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No pending trainings yet.
                  </Typography>
                </Box>
              )}
            </TabPanel>
            <TabPanel value="2" sx={{ p: 0 }}>
              {/* Completed Training Section */}

              {(completedTrainings?.length ?? 0) > 0 ? (
                <>
                  <Typography
                    variant="body1"
                    sx={{ color: "text.secondary", marginBottom: "20px" }}
                  >
                    Trainings you have already completed and acknowledged.
                  </Typography>

                  <Grid container spacing={2}>
                    {completedTrainings?.map((training, index) => (
                      <Grid item xs={12} key={training?.id ?? index}>
                        <TrainingCard
                          training={training}
                          isAdmin={false}
                          onClick={handleOnClick}
                          TrainingSectionAttemptStatusMap={
                            TrainingSectionAttemptStatusMap
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              ) : (
                <Box
                  sx={{
                    height: "300px", // Or match your card/grid height
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No completed trainings yet.
                  </Typography>
                </Box>
              )}
            </TabPanel>
          </TabContext>
        </Box>
      </Box>
    </>
  );
};

export default TrainingEmployeeHome;
